import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, User, Users, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { auth, portal } from '@/lib/api';

export default function LoginPage() {
  const nav = useNavigate();
  const [staffEmail, setStaffEmail] = React.useState('priya@nairmigration.com.au');
  const [staffPw, setStaffPw] = React.useState('immipro-demo-2025');
  const [staffErr, setStaffErr] = React.useState('');
  const [staffLoading, setStaffLoading] = React.useState(false);

  const [clientEmail, setClientEmail] = React.useState('john.smith@example.com');
  const [magicToken, setMagicToken] = React.useState('');
  const [clientMsg, setClientMsg] = React.useState('');
  const [clientErr, setClientErr] = React.useState('');
  const [clientLoading, setClientLoading] = React.useState(false);

  const doStaffLogin = async (e) => {
    e.preventDefault();
    setStaffErr(''); setStaffLoading(true);
    try {
      await auth.login(staffEmail, staffPw);
      nav('/app');
    } catch (err) {
      setStaffErr(err?.response?.data?.detail || 'Login failed');
    } finally { setStaffLoading(false); }
  };

  const requestMagic = async () => {
    setClientErr(''); setClientMsg(''); setClientLoading(true);
    try {
      const r = await portal.requestMagic(clientEmail);
      if (r.delivery?.sent) {
        setClientMsg(`Magic link sent to ${r.email} via ${r.delivery.provider}. Check your inbox and click the button in the email.`);
        setMagicToken(''); // no local token needed \u2014 they'll click the emailed link
      } else {
        setMagicToken(r.magic_token || '');
        setClientMsg(r.message || 'Email provider is not configured yet \u2014 click continue to complete sign-in.');
      }
    } catch (err) {
      setClientErr(err?.response?.data?.detail || 'Magic link request failed');
    } finally { setClientLoading(false); }
  };

  const useMagic = async () => {
    setClientErr(''); setClientLoading(true);
    try {
      await portal.verifyMagic(magicToken);
      nav('/portal');
    } catch (err) {
      setClientErr(err?.response?.data?.detail || 'Magic link verification failed');
    } finally { setClientLoading(false); }
  };

  return (
    <section className="mx-auto max-w-md px-6 py-16">
      <div className="text-center">
        <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Welcome back</h1>
        <p className="mt-2 text-sm text-slate-600">Sign in to continue.</p>
      </div>

      <Card className="mt-8 border-slate-200 shadow-lg">
        <CardContent className="p-6">
          <Tabs defaultValue="staff">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="staff" className="gap-1"><Users className="h-3.5 w-3.5" /> Staff</TabsTrigger>
              <TabsTrigger value="client" className="gap-1"><User className="h-3.5 w-3.5" /> Client</TabsTrigger>
            </TabsList>

            <TabsContent value="staff" className="mt-5 space-y-4">
              <form className="space-y-4" onSubmit={doStaffLogin}>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={staffEmail} onChange={(e) => setStaffEmail(e.target.value)} className="mt-1" required />
                </div>
                <div>
                  <Label htmlFor="pw">Password</Label>
                  <Input id="pw" type="password" value={staffPw} onChange={(e) => setStaffPw(e.target.value)} className="mt-1" required />
                </div>
                {staffErr && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{staffErr}</div>}
                <Button type="submit" disabled={staffLoading} className="h-11 w-full gap-2 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
                  {staffLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                  Sign in
                </Button>
                <div className="text-center text-[11px] text-slate-400">
                  Demo credentials pre-filled · Priya Nair, Nair Migration Advisory
                </div>
              </form>
            </TabsContent>

            <TabsContent value="client" className="mt-5 space-y-4">
              <div>
                <Label htmlFor="cemail">Your email</Label>
                <Input id="cemail" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} className="mt-1" />
              </div>
              {!magicToken && (
                <Button onClick={requestMagic} disabled={clientLoading} className="h-11 w-full gap-2 bg-[#2ECC9A] text-[#1A3A5C] hover:bg-[#2ECC9A]/90">
                  {clientLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Send magic link
                </Button>
              )}
              {magicToken && (
                <>
                  {clientMsg && <div className="rounded-md bg-[#2ECC9A]/10 p-3 text-xs text-[#1A3A5C]">{clientMsg}</div>}
                  <div className="rounded-md bg-slate-100 p-2 font-mono text-[10px] break-all text-slate-500">{magicToken.slice(0, 80)}…</div>
                  <Button onClick={useMagic} disabled={clientLoading} className="h-11 w-full gap-2 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
                    {clientLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Continue to portal
                  </Button>
                </>
              )}
              {clientErr && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{clientErr}</div>}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <div className="mt-6 text-center text-xs text-slate-500">
        Don&rsquo;t have an account? <Link to="/demo" className="font-medium text-[#1A3A5C] hover:underline">Request a demo</Link>
      </div>
    </section>
  );
}
